import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {TicketType} from "types/TicketType";
import {useNotifications} from "context/NotificationContext";
import Button from "components/Button";
import {PencilIcon, TrashIcon} from "@heroicons/react/solid";
import Page from "components/Page";
import Card from "components/Card";
import DescriptionList from "components/DescriptionList";
import DescriptionListItem from "components/DescriptionListItem";
import DescriptionTerm from "components/DescriptionTerm";
import DescriptionDetails from "components/DescriptionDetails";
import {formattedDate} from "services/date";
import DeleteTicketType from "pages/TicketType/Delete";
import StatBox from "components/StatBox";
import {IdentificationIcon, UserIcon} from "@heroicons/react/outline";
import StatList from "components/StatList";
import Chip from "components/Chip";
import useMoney from "../../../hooks/useMoney";

function ViewTicketType() {
    const {id} = useParams()
    const {formattedMoney} = useMoney()
    const {data: ticketType, isLoading} = useQuery(
        ['ticket-type', {id}],
        () => ngage.ticketTypes.get(parseInt(id as string))
    )

    const [deletingTicketType, setDeletingTicketType] = useState<TicketType | null>(null);
    const {notify} = useNotifications()
    const navigate = useNavigate()

    // Prepare action handlers
    function handleDeleteTicketType() {
        if(ticketType) setDeletingTicketType(ticketType)
    }

    function handleEditTicketType() {
        if(id) navigate(`/ticket-types/${id}/edit`)
    }

    // Prepare page actions
    const actions = <>
        <Button color={Button.colors.WHITE} icon={TrashIcon} onClick={handleDeleteTicketType} disabled={!ticketType || ticketType.ticket_count > 0}>Delete</Button>
        <Button color={Button.colors.PRIMARY} icon={PencilIcon} onClick={handleEditTicketType}>Edit</Button>
    </>

    return (
        <Page
            title={ticketType?.name || "View Ticket Type"}
            breadcrumbs={{"Audience": "/", "Ticket Types": "/ticket-types", [ticketType?.name || "View Ticket Type"]: `/ticket-types/${id}`}}
            actions={actions}
            isLoading={isLoading}
        >
            {!!ticketType &&
                <>
                    <section>
                        <StatList>
                            <StatBox name="Tickets" icon={IdentificationIcon} stat={ticketType.ticket_count} />
                            <StatBox name="Holders" icon={UserIcon} stat={ticketType.holder_count} />
                            {/*<StatBox name="Usage Rate" icon={PresentationChartLineIcon} stat={"96.6%"} />*/}
                        </StatList>
                    </section>

                    <section className="mt-4">
                        <Card
                            heading="Ticket Type Information"
                            subheading={`A type used for ${ticketType.ticket_count?.toLocaleString()} tickets`}
                        >
                            <DescriptionList>
                                <DescriptionListItem span={2}>
                                    <DescriptionTerm>Description</DescriptionTerm>
                                    <DescriptionDetails>
                                        <p className="whitespace-pre-line">{ticketType.description ?? `A type of single-use ticket`}</p>
                                    </DescriptionDetails>
                                </DescriptionListItem>

                                <DescriptionListItem span={1}>
                                    <DescriptionTerm>Price</DescriptionTerm>
                                    <DescriptionDetails>
                                        {ticketType.price? formattedMoney(ticketType.price, ticketType.currency) : 'Free'}
                                    </DescriptionDetails>
                                </DescriptionListItem>

                                <DescriptionListItem span={1}>
                                    <DescriptionTerm>Created</DescriptionTerm>
                                    <DescriptionDetails>
                                        {formattedDate(ticketType.created_at)}
                                    </DescriptionDetails>
                                </DescriptionListItem>
                            </DescriptionList>
                        </Card>
                    </section>

                    {!!ticketType.organisations?.length &&
                        <section className="mt-4">
                            <Card heading="Organisations" subheading="The organisations for which this offer applies">
                                <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {ticketType.organisations.map((organisation, index) => (
                                        <li key={index}>
                                            <Chip
                                                name={organisation.name}
                                                href={`/organisations/${organisation.id}`}
                                                backgroundSrc={organisation.logo?.preview_url}
                                            />
                                        </li>
                                    ))}
                                </ul>
                            </Card>
                        </section>
                    }
                </>
            }

            {/* Delete confirmation modal */}
            <DeleteTicketType
                ticketType={deletingTicketType}
                show={ticketType === deletingTicketType}
                onCancel={() => setDeletingTicketType(null)}
                onDelete={ticketType => {
                    navigate('/ticket-types')
                    notify("success", "Ticket Type Deleted", `${ticketType.name} has been permanently removed`)
                }}
                onDeleteFail={ticketType => {
                    setDeletingTicketType(null)
                    notify("error", "Ticket Type Not Deleted", `${ticketType.name} could not be removed`)
                }}
            />
        </Page>
    );
}

export default ViewTicketType;